import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Alert } from '../../components/common';
import { LoginCredentials } from '../../types';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasRedirectedRef = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>();

  // Redirigir si ya está autenticado - CON PROTECCIÓN CONTRA BUCLES
  useEffect(() => {
    if (isAuthenticated && !isLoading && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;

      // Obtener la página de origen si existe
      const from = (location.state as any)?.from || '/home';

      navigate(from, { replace: true });
    }

    // Reset el flag si el usuario se desautentica
    if (!isAuthenticated && !isLoading) {
      hasRedirectedRef.current = false;
    }
  }, [isAuthenticated, isLoading]); // REMOVIDO navigate de las dependencias

  // Limpiar errores al desmontar - CON DEPENDENCIA ESTABLE
  useEffect(() => {
    return () => clearError();
  }, []); // Array vacío para que solo se ejecute al desmontar

  const onSubmit = async (data: LoginCredentials) => {
    try {
      setIsSubmitting(true);
      clearError();

      await login(data.cedula, data.password);

    } catch (error: any) {
      console.error('Error en login:', error);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      if (navigator.vibrate) {
        navigator.vibrate(200);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Si está cargando, mostrar spinner
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brinsa-celeste/10">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brinsa-azul mx-auto"></div>
          <p className="mt-4 text-brinsa-azulOscuro/80">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  // Si ya está autenticado y se está redirigiendo, mostrar spinner
  if (isAuthenticated && hasRedirectedRef.current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brinsa-celeste/10">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brinsa-azul mx-auto"></div>
          <p className="mt-4 text-brinsa-azulOscuro/80">Redirigiendo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Panel izquierdo con branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br justify-center from-brinsa-azul to-brinsa-azulOscuro relative overflow-hidden rounded-tr-[30px] rounded-br-[30px]">
        <div className="absolute inset-0 bg-brinsa-azulOscuro/10" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img
              src="\assets\images\logo_blanco_azul.png"
              alt="Brinsa"
              className="h-16 w-auto mb-8"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <div className="hidden">
              <h1 className="text-4xl font-bold mb-4">Brinsa</h1>
            </div>

            <p className="text-xl mb-8 opacity-90">
              Transformamos recursos de forma sostenible para generar bienestar
            </p>
          </motion.div>
        </div>
      </div>

      {/* Panel derecho con formulario */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="mx-auto w-full max-w-md"
        >
          {/* Logo móvil */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="\assets\images\logo_azul.png"
              alt="Brinsa"
              className="h-12 w-auto mx-auto mb-4"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <div className="hidden">
              <h1 className="text-2xl font-bold text-brinsa-azul">Brinsa</h1>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-brinsa-azul text-center">
              ¡Hola!
            </h2>
            <p className="mt-2 text-center text-brinsa-azulOscuro/80">
              Ingresa tus datos para iniciar sesión.
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <Alert
                type="error"
                message={error}
                onClose={clearError}
              />
            )}

            <div className="space-y-4">
              {/* Campo de cédula */}
              <div>
                <label htmlFor="cedula" className="block text-sm font-medium text-brinsa-azul">
                  Número de cédula
                </label>
                <div className="mt-1 relative">
                  <input
                    {...register('cedula', {
                      required: 'La cédula es requerida',
                      minLength: {
                        value: 6,
                        message: 'La cédula debe tener al menos 6 caracteres'
                      }
                    })}
                    type="text"
                    className="appearance-none relative block w-full px-3 py-3 border border-slate-300 placeholder-slate-500 text-brinsa-azul rounded-lg focus:outline-none focus:ring-brinsa-celeste focus:border-brinsa-celeste focus:z-10 text-base"
                    placeholder="00000000"
                  />
                  {errors.cedula && (
                    <p className="mt-1 text-sm text-brinsa-rojo">{errors.cedula.message}</p>
                  )}
                </div>
              </div>

              {/* Campo de contraseña */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-brinsa-azul">
                  Contraseña
                </label>
                <div className="mt-1 relative">
                  <input
                    {...register('password', {
                      required: 'La contraseña es requerida',
                      minLength: {
                        value: 6,
                        message: 'La contraseña debe tener al menos 6 caracteres'
                      }
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className="appearance-none relative block w-full px-3 py-3 pr-10 border border-slate-300 placeholder-slate-500 text-brinsa-azul rounded-lg focus:outline-none focus:ring-brinsa-celeste focus:border-brinsa-celeste focus:z-10 text-base"
                    placeholder="••••••••••••"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-slate-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-slate-400" />
                    )}
                  </button>
                  {errors.password && (
                    <p className="mt-1 text-sm text-brinsa-rojo">{errors.password.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Botón de submit */}
            <Button
              type="submit"
              className="w-full justify-center"
              size="lg"
              isLoading={isSubmitting}
              rightIcon={<ArrowRight size={20} />}
            >
              INICIAR PRUEBA
            </Button>
          </form>

          {/* Pie de página */}
          <div className="mt-8 text-center text-sm text-brinsa-azulOscuro/70">
            <img
              src="\assets\images\universidad_brinsa.png"
              alt="Brinsa"
              className="h-32 w-32 mx-auto mb-4"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;